import React, { useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useGeolocation from '../hooks/useGeolocation';
import MapView from '../components/Map/MapView';
import SpotList from '../components/Spot/SpotList';
import Gallery from '../components/Gallery/Gallery';
import Lightbox from '../components/Gallery/Lightbox';
import AddSpotModalV2 from '../components/Modals/AddSpotModalV2';
import SpotDetailsModal from '../components/Modals/SpotDetailsModal';
import AuthModal from '../components/Auth/AuthModal';
import LegalModal from '../components/Modals/LegalModal';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import { trackPageView } from '../services/tracking';

function MainPage() {
  const { state, actions } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { location: geoLocation, error: geoError, loading: geoLoading, getCurrentLocation, watchLocation, clearWatch } = useGeolocation();

  // Initialize data on component mount
  useEffect(() => {
    let mounted = true;
    
    // Track page view
    trackPageView('home', { view: state.view });
    
    const initializeData = async () => {
      if (state.spots.length === 0 && !state.loading && mounted) {
        await actions.fetchSpots();
      }
    };
    
    initializeData();
    
    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array - only run once

  // Request user location on first load for proximity sorting
  useEffect(() => {
    // Only request geolocation if we don't already have it and it's supported
    if (!state.userLocation && navigator.geolocation && state.spots.length > 0) {
      // Small delay to let the app load first
      const timer = setTimeout(() => {
        getCurrentLocation();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [state.spots.length, state.userLocation, getCurrentLocation]);

  // Update app state when geolocation changes
  useEffect(() => {
    if (geoLocation && (!state.userLocation || geoLocation.timestamp > (state.userLocation.timestamp || 0))) {
      actions.setUserLocation([geoLocation.latitude, geoLocation.longitude]);
      actions.setUserLocationAccuracy(geoLocation.accuracy);
      
      // Automatically switch to proximity sorting when location is obtained (if currently on default)
      if (state.sortMode !== 'proximity' && state.spots.length > 0) {
        actions.setSortMode('proximity');
        actions.setSortOrder('asc');
      }
    }
  }, [geoLocation, state.userLocation, state.sortMode, state.spots.length, actions]);

  // Optional: Set up position watching for real-time updates
  useEffect(() => {
    let watchId = null;
    
    // Only watch if user has already granted permission and we want real-time updates
    if (state.userLocation && navigator.geolocation) {
      // Uncomment to enable real-time position tracking
      // watchId = watchLocation();
    }

    return () => {
      if (watchId) {
        clearWatch(watchId);
      }
    };
  }, [state.userLocation, watchLocation, clearWatch]);

  // Handle URL parameters for sharing functionality
  useEffect(() => {
    if (!state.urlParamsProcessed && state.spots.length > 0) {
      const spotId = searchParams.get('spot');
      const showDetails = searchParams.get('details') === 'true';
      
      if (spotId && showDetails) {
        const spot = state.spots.find(s => s.id === spotId);
        if (spot) {
          actions.setSelectedSpot(spot);
          actions.setShowSpotDetails(true);
          
          // Clean up URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
      actions.setUrlParamsProcessed(true);
    }
  }, [state.spots, state.urlParamsProcessed, searchParams, actions]);

  // Process all photos for TikTok gallery
  useEffect(() => {
    if (state.spots.length > 0) {
      const allPhotos = [];
      state.spots.forEach(spot => {
        if (spot.photos && Array.isArray(spot.photos)) {
          spot.photos.forEach((photo, photoIndex) => {
            allPhotos.push({
              ...photo,
              spot: spot,
              photoIndex: photoIndex
            });
          });
        }
      });
      
      // Shuffle array for random photo order
      const shuffledPhotos = allPhotos.sort(() => Math.random() - 0.5);
      actions.setAllPhotosWithSpots(shuffledPhotos);
    }
  }, [state.spots, actions]);

  // Get user location (only once)
  useEffect(() => {
    let mounted = true;
    
    if (!state.mapInitialized && !state.userLocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (mounted) {
            const userPos = [position.coords.latitude, position.coords.longitude];
            actions.setUserLocation(userPos);
            actions.setMapCenter(userPos);
            actions.setMapInitialized(true);
          }
        },
        (error) => {
          if (mounted) {
            console.error('Error getting location:', error);
            actions.setMapInitialized(true);
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes cache
        }
      );
    } else if (!state.mapInitialized) {
      // No geolocation support, mark as initialized
      actions.setMapInitialized(true);
    }
    
    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array - only run once

  // Handle resize functionality
  const handleMouseDown = (e) => {
    e.preventDefault();
    actions.setIsDragging(true);
  };

  const handleMouseMove = React.useCallback((e) => {
    if (!state.isDragging) return;
    
    const windowWidth = window.innerWidth;
    const newMapWidth = (e.clientX / windowWidth) * 100;
    
    // Limits: map between 33.33% and 66.67% (prevents menu from shrinking below default size)
    if (newMapWidth >= 33.33 && newMapWidth <= 66.67) {
      actions.setMapWidth(newMapWidth);
    }
  }, [state.isDragging, actions]);

  const handleMouseUp = React.useCallback(() => {
    actions.setIsDragging(false);
  }, [actions]);

  // Mouse event listeners for dragging
  React.useEffect(() => {
    if (state.isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [state.isDragging, handleMouseMove, handleMouseUp]);

  // Early return AFTER all hooks
  if (state.showTikTokGallery) {
    return <Gallery />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="flex h-[calc(100vh-120px)]">
        {/* Map Panel - LEFT SIDE */}
        <div 
          className="relative"
          style={{ width: `${state.mapWidth}%` }}
        >
          <MapView />
        </div>

        {/* Resizable Separator */}
        <div
          className={`w-1 bg-gray-300 hover:bg-gray-400 cursor-col-resize flex items-center justify-center ${
            state.isDragging ? 'bg-blue-400' : ''
          }`}
          onMouseDown={handleMouseDown}
        >
          <div className="w-0.5 h-8 bg-white rounded-full shadow-sm"></div>
        </div>

        {/* Spots Panel - RIGHT SIDE */}
        <div 
          className="bg-white border-l border-gray-200 overflow-hidden"
          style={{ width: `${100 - state.mapWidth}%` }}
        >
          <SpotList />
        </div>
      </div>

      <Footer />

      {/* Modals */}
      {state.showLightbox && <Lightbox />}
      {state.showAddSpot && <AddSpotModalV2 
        isOpen={state.showAddSpot} 
        onClose={(success) => {
          actions.setShowAddSpot(false);
          if (success) {
            actions.fetchSpots(); // Rafraîchir la liste après création
          }
        }}
        initialPosition={state.mapCenter}
      />}
      {state.showSpotDetails && <SpotDetailsModal />}
      {state.showAuth && <AuthModal />}
      {state.showLegalModal && <LegalModal />}
    </div>
  );
}

export default MainPage;